// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { Component } from "react";
import { CommandBar } from "office-ui-fabric-react/lib/";
import { withTranslation } from "react-i18next";

import { ConfigurationFormComponent } from "../ConfigurationFormComponent/ConfigurationFormComponent";
import { PreferencesFormComponent } from "../PreferencesFormComponent/PreferencesFormComponent";
import { configService } from "../../services/ConfigService";
import { eventService } from "../../services/EventService";
import { apiService } from "../../services/ApiService";
import { importService } from "../../services/ImportService";
import { print } from "../../services/LoggingService";
import DeleteAllTwinsComponent from "./DeleteAllTwinsComponent/DeleteAllTwinsComponent";
import UploadSummaryModal from "./UploadSummaryModal";

import "./AppCommandBar.scss";
import {
  Keyboard24Regular,
  Delete24Regular,
  PersonArrowRight24Regular,
  Settings24Regular,
  ArrowUpload24Regular,
  Link24Regular,
  SignOut24Regular,
  Person24Regular
} from "@fluentui/react-icons";
import { KeyboardShortcutsComponents } from "../KeyboardShortcutsComponents/KeyboardShortcutsComponents";
import { authService } from "../../services/AuthService";

class AppCommandBar extends Component {

  constructor(props) {
    super(props);
    this.delete = React.createRef();
    this.configure = React.createRef();
    this.preferences = React.createRef();
    this.keyboard = React.createRef();
    this.uploadTwinsRef = React.createRef();
    this.uploadRelationshipsRef = React.createRef();
    this.state = {
      farItems: [
        {
          key: "keyboardShortcuts",
          text: this.props.t("appCommandBar.keyboardShortcuts.text"),
          ariaLabel: this.props.t("appCommandBar.keyboardShortcuts.ariaLabel"),
          onRenderIcon: () => <Keyboard24Regular />,
          onClick: () => this.keyboard.current.open(),
          iconOnly: true,
          className: "app-toolbarButtons"
        },
        {
          key: "deleteTwins",
          text: this.props.t("appCommandBar.deleteTwins.text"),
          ariaLabel: this.props.t("appCommandBar.deleteTwins.ariaLabel"),
          onRenderIcon: () => <Delete24Regular />,
          onClick: () => this.delete.current.open(),
          iconOnly: true,
          className: "app-toolbarButtons delete-button"
        },
        {
          key: "uploadTwins",
          text: this.props.t("appCommandBar.uploadTwins.text"),
          ariaLabel: this.props.t("appCommandBar.uploadTwins.ariaLabel"),
          onRenderIcon: () => <ArrowUpload24Regular />,
          onClick: () => this.uploadTwinsRef.current.click(),
          iconOnly: true,
          className: "app-toolbarButtons"
        },
        {
          key: "uploadRelationships",
          text: this.props.t("appCommandBar.uploadRelationships.text"),
          ariaLabel: this.props.t("appCommandBar.uploadRelationships.ariaLabel"),
          onRenderIcon: () => <Link24Regular />,
          onClick: () => this.uploadRelationshipsRef.current.click(),
          iconOnly: true,
          className: "app-toolbarButtons"
        },
        {
          key: "userProfile",
          text: authService.getUser()?.name || "User",
          ariaLabel: "User Profile",
          onRenderIcon: () => <Person24Regular />,
          className: "app-toolbarButtons",
          disabled: true
        },
        {
          key: "signOut",
          text: "Sign Out",
          ariaLabel: "Sign Out",
          iconOnly: true,
          onRenderIcon: () => <SignOut24Regular />,
          onClick: () => authService.logout(),
          className: "app-toolbarButtons"
        },
        {
          key: "settings",
          text: this.props.t("appCommandBar.settings.text"),
          ariaLabel: this.props.t("appCommandBar.settings.ariaLabel"),
          iconOnly: true,
          onRenderIcon: () => <Settings24Regular />,
          onClick: () => this.togglePreferencesModal(),
          className: "app-toolbarButtons settings-button"
        }
      ],
      showUploadSummary: false,
      uploadResults: null
    };
  }

  updateAdtUrlSettings = async () => {
    try {
      const { appAdtUrl } = await configService.getConfig();
      this.configure.current.loadConfigurationSettings({ type: "start", appAdtUrl });
    } catch (exc) {
      if (exc.errorCode !== "user_cancelled") {
        exc.customMessage = "Error on saving settings";
        eventService.publishError(exc);
      }
    }
  }

  togglePreferencesModal = () => {
    this.preferences.current.loadExistingSettings();
  }

  handleUploadTwins = async evt => {
    const file = evt.target.files[0];
    if (file) {
      eventService.publishLoading(true);
      try {
        const data = await importService.tryLoad(file);
        if (data && data.digitalTwinsGraph && data.digitalTwinsGraph.digitalTwins) {
          const result = await apiService.batchUploadTwins(data.digitalTwinsGraph.digitalTwins);
          if (result) {
            console.log("Upload result:", result);
            print(`*** Bulk upload complete (${result.successCount} success, ${result.failureCount} failed)`, "info");
            this.setState({ uploadResults: result, showUploadSummary: true });
          } else {
            print("*** Bulk upload failed", "error");
          }
        } else {
          print("*** No twins found in file", "warning");
        }
      } catch (exc) {
        exc.customMessage = "Error uploading twins";
        eventService.publishError(exc);
      }
      eventService.publishLoading(false);
    }
    evt.target.value = "";
  }

  handleUploadRelationships = async evt => {
    const file = evt.target.files[0];
    if (file) {
      eventService.publishLoading(true);
      try {
        const data = await importService.tryLoad(file);
        if (data && data.digitalTwinsGraph && data.digitalTwinsGraph.relationships) {
          const result = await apiService.uploadRelationships(data.digitalTwinsGraph.relationships);
          if (result) {
            print(`*** Bulk upload complete (${result.successCount} success, ${result.failureCount} failed)`, "info");
            this.setState({ uploadResults: result, showUploadSummary: true });
          } else {
            print("*** Bulk upload failed", "error");
          }
        } else {
          print("*** No relationships found in file", "warning");
        }
      } catch (exc) {
        exc.customMessage = "Error uploading relationships";
        eventService.publishError(exc);
      }
      eventService.publishLoading(false);
    }
    evt.target.value = "";
  }

  render() {
    const { farItems } = this.state;
    return (
      <div className="app-commandbar-container">
        <CommandBar
          farItems={farItems}
          ariaLabel={this.props.t("appCommandBar.commandBar.ariaLabel")}
          className="app-commandbar" />
        <input type="file" name="twin-upload" className="app-fileInput" accept="application/json, .json, .xlsx"
          ref={this.uploadTwinsRef} onChange={this.handleUploadTwins} style={{ display: "none" }} />
        <input type="file" name="relationship-upload" className="app-fileInput" accept="application/json, .json, .xlsx"
          ref={this.uploadRelationshipsRef} onChange={this.handleUploadRelationships} style={{ display: "none" }} />
        <ConfigurationFormComponent t={this.props.t} ref={this.configure} />
        <PreferencesFormComponent ref={this.preferences}
          toggleHighContrastMode={this.props.toggleHighContrastMode}
          toggleOptionalComponent={this.props.toggleOptionalComponent}
          optionalComponentsState={this.props.optionalComponentsState}
          contrast={this.props.contrast}
          t={this.props.t} />
        <DeleteAllTwinsComponent ref={this.delete} t={this.props.t} />
        <KeyboardShortcutsComponents ref={this.keyboard} t={this.props.t} />
        <UploadSummaryModal
          isVisible={this.state.showUploadSummary}
          results={this.state.uploadResults}
          onClose={() => this.setState({ showUploadSummary: false })}
          t={this.props.t} />
      </div>
    );
  }

}

export default withTranslation()(AppCommandBar);
